import React from "react";
import { Alert, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import * as Sentry from "@sentry/react-native";
import * as Clipboard from "expo-clipboard";
import { Feather, MaterialIcons } from "@expo/vector-icons";

import {
  type DiagnosticItem,
  DiagnosticStatus,
} from "@/schemas/app/diagnostic";

type DiagnosticItemProps = {
  item: DiagnosticItem;
};

function DiagnosticItemComponent({
  item,
}: DiagnosticItemProps): React.JSX.Element {
  const handleCopy = async (value: string): Promise<void> => {
    try {
      await Clipboard.setStringAsync(value);
      // Provide feedback to the user
      Alert.alert("Copied to Clipboard", `Value "${value}" has been copied.`);
    } catch (error) {
      Sentry.captureException(error);
      Alert.alert("Error", "Failed to copy to clipboard.");
    }
  };

  return (
    <View style={styles.item}>
      <View style={styles.itemHeader}>
        <Text style={styles.itemName}>{item.name}</Text>
        <View style={styles.itemStatus}>
          <Feather
            name={
              item.status === DiagnosticStatus.OK
                ? "check-circle"
                : "alert-circle"
            }
            size={16}
            color={
              item.status === DiagnosticStatus.OK
                ? "#4CAF50"
                : item.status === DiagnosticStatus.WARNING
                  ? "#FFC107"
                  : "#F44336"
            }
          />
          <Text
            style={[
              styles.itemStatusText,
              {
                color:
                  item.status === DiagnosticStatus.OK
                    ? "#4CAF50"
                    : item.status === DiagnosticStatus.WARNING
                      ? "#FFC107"
                      : "#F44336",
              },
            ]}
          >
            {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
          </Text>
        </View>
      </View>
      <View style={styles.container}>
        <Text style={styles.itemValue} numberOfLines={1} ellipsizeMode="tail">
          {item.value}
        </Text>
        <TouchableOpacity
          onPress={() => handleCopy(item.value)}
          style={styles.copyButton}
        >
          <MaterialIcons name={"content-copy"} size={18} color="#000" />
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
  },
  item: {
    backgroundColor: "#fff",
    borderRadius: 8,
    padding: 16,
    marginBottom: 8,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  itemHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  itemName: {
    fontSize: 16,
    fontWeight: "500",
  },
  itemValue: {
    fontSize: 14,
    color: "#666",
    flex: 1, // Allow the text to take up available space
    marginRight: 8, // Add some space between the text and the copy button
  },
  itemStatus: {
    flexDirection: "row",
    alignItems: "center",
  },
  itemStatusText: {
    marginLeft: 4,
    fontSize: 12,
  },
  copyButton: {
    padding: 8,
    borderRadius: 8,
  },
});

export default DiagnosticItemComponent;
